import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { OutOfCreditsModal } from "@/components/out-of-credits-modal";
import { UpgradeModal } from "@/components/upgrade-modal";

// Module-scope reference to the active provider's trigger callback. Lets
// non-React code (Zustand stores, fetch wrappers, etc.) raise the gate
// without needing to live inside the React tree or thread the hook through
// every call site. The provider registers itself in useEffect and clears
// the ref on unmount.
let _globalTrigger: ((balance: number, needed: number) => void) | null = null;

/**
 * Raise the out-of-credits modal from non-React code. No-op if no provider
 * is currently mounted.
 */
export function triggerCreditsGate(balance: number, needed: number): void {
  if (_globalTrigger) _globalTrigger(balance, needed);
}

type CreditsGateState = {
  balance: number;
  needed: number;
} | null;

type CreditsGateContextValue = {
  /** Call when a fetch response returns 402 CREDITS_EXHAUSTED. */
  trigger: (balance: number, needed: number) => void;
  /** True while the out-of-credits modal is visible. */
  isBlocked: boolean;
};

const CreditsGateContext = createContext<CreditsGateContextValue | null>(null);

export function useCreditsGate(): CreditsGateContextValue {
  const ctx = useContext(CreditsGateContext);
  if (!ctx) throw new Error("useCreditsGate must be used inside <CreditsGateProvider>");
  return ctx;
}

export function CreditsGateProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<CreditsGateState>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const trigger = useCallback((balance: number, needed: number) => {
    setState({ balance, needed });
  }, []);

  // Register the trigger globally so non-React code (canvas-store fetch
  // handler, askmoji fetch handler, etc.) can raise the modal without
  // needing to live in the React tree or pass the callback down.
  useEffect(() => {
    _globalTrigger = trigger;
    return () => {
      if (_globalTrigger === trigger) _globalTrigger = null;
    };
  }, [trigger]);

  const value = useMemo<CreditsGateContextValue>(
    () => ({ trigger, isBlocked: state !== null }),
    [trigger, state]
  );

  return (
    <CreditsGateContext.Provider value={value}>
      {children}
      {state && (
        <OutOfCreditsModal
          balance={state.balance}
          needed={state.needed}
          onClose={() => setState(null)}
          onUpgrade={() => {
            setState(null);
            setShowUpgrade(true);
          }}
        />
      )}
      {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
    </CreditsGateContext.Provider>
  );
}

/**
 * Parse a Response to detect a 402 CREDITS_EXHAUSTED and trigger the gate.
 * Returns true if the response was a credits-exhausted signal (caller should abort).
 * Safe to call on any Response; a no-op for non-402s.
 */
export async function handleCreditsResponse(
  res: Response,
  trigger: (balance: number, needed: number) => void
): Promise<boolean> {
  if (res.status !== 402) return false;
  try {
    const clone = res.clone();
    const body = await clone.json();
    if (body?.code === "CREDITS_EXHAUSTED") {
      trigger(Number(body.balance ?? 0), Number(body.needed ?? 0));
      return true;
    }
  } catch {
    // ignore parse errors — fall through
  }
  return false;
}
